import { z } from 'zod';

/**
 * The reviewer's work lists (F7) — "whose turn is it", which a case status
 * alone cannot say: an `in_review` case may be waiting on the reviewer (evidence
 * to judge), on the member (fee to pay, time to pick) or on the clock (an
 * appointment to hold). Membership is decided by the server from the steps and
 * the live interview; a client only names the list it wants.
 *
 * - `evidence` — something to judge: fresh evidence, or a re-review's edit.
 * - `interviews` — an appointment to hold or a held one to conclude, ordered by
 *   its time rather than by how long the member has waited.
 * - `member` — nothing for staff to do: the member is uploading, redoing,
 *   paying or booking. Listed so a reviewer can find a case, not so they work it.
 */
export const VERIFICATION_QUEUES = ['evidence', 'interviews', 'member'] as const;

export const VerificationQueueSchema = z.enum(VERIFICATION_QUEUES);

export type VerificationQueue = (typeof VERIFICATION_QUEUES)[number];

const QUEUE_SET: ReadonlySet<string> = new Set<string>(VERIFICATION_QUEUES);

export function isVerificationQueue(value: string): value is VerificationQueue {
  return QUEUE_SET.has(value);
}

/** How many cases sit in each list — what the tab labels carry. */
export const VerificationQueueCountsSchema = z
  .object({
    evidence: z.number().int().nonnegative(),
    interviews: z.number().int().nonnegative(),
    member: z.number().int().nonnegative(),
  })
  .strict();

export type VerificationQueueCounts = z.infer<typeof VerificationQueueCountsSchema>;
